/**
 * Column sizing utilities for consistent table width management
 */

/**
 * Get optimal column width based on column type and content
 * @param {string} columnId - The column identifier
 * @param {object} customWidths - Optional custom widths override
 * @returns {number} Optimal width in pixels
 */
export const getOptimalColumnWidth = (columnId, customWidths = {}) => {
  if (customWidths[columnId]) {
    return customWidths[columnId];
  }
  
  // Default optimal widths
  const defaultWidths = {
    // ID columns
    'id': 80,
    
    // Name/Title columns
    'titolo': 250,
    'nome': 200,
    'artista': 200,
    'cognome': 150,
    'denominazione_sociale': 200,
    'cliente_display': 200,
    'descrizione': 250,
    
    // Date columns
    'data_ordine': 110,
    'data_pagamento': 110,
    'data_emissione': 110,
    'data_scadenza': 110,
    'data': 110,
    
    // Currency columns
    'prezzo': 100,
    'prezzo_negozio': 100,
    'prezzo_ibs': 100,
    'totale': 110,
    'totale_ordine': 110,
    'subtotale': 110,
    'acconto': 100,
    'margine': 110,
    'costo': 100,
    'importo': 110,
    
    // Percentage columns
    'margine_percentuale': 120,
    'iva': 80,
    'sconto': 90,
    
    // Status/Category columns
    'stato': 140,
    'categoria': 130,
    'genere': 130,
    'supporto': 110,
    'condizione': 120,
    'origine': 120,
    
    // Contact columns
    'email': 200,
    'telefono': 130,
    'cellulare': 130,
    
    // Address columns
    'indirizzo': 200,
    'citta': 130,
    'provincia': 90,
    'cap': 90,
    'nazione': 110,
    
    // Payment/Shipping columns
    'payment_method': 150,
    'corriere_denominazione': 150,
    'corriere': 150,
    'metodo_pagamento': 150,
    
    // Other common columns
    'quantita': 90,
    'anno': 90,
    'ean': 130,
    'codice_fiscale': 140,
    'partita_iva': 130,
    'numero': 110,
    'numero_ordine_marketplace': 180,
  };
  
  return defaultWidths[columnId] || 120;
};

/**
 * Get minimum column width based on column type
 * @param {string} columnId - The column identifier
 * @returns {number} Minimum width in pixels
 */
export const getMinColumnWidth = (columnId) => {
  const minWidths = {
    'id': 70,
    'cliente_display': 150,
    'titolo': 180,
    'nome': 150,
    'artista': 150,
    'data_ordine': 100,
    'data': 100,
    'origine': 100,
    'stato': 120,
    'email': 150,
    'denominazione_sociale': 150,
  };
  
  return minWidths[columnId] || 90;
};

/**
 * Auto-fit all visible columns to viewport width
 * @param {object} table - TanStack table instance
 * @param {object} customWidths - Optional custom optimal widths
 * @param {number} reservedSpace - Space to reserve for sticky columns and scrollbar
 */
export const autoFitColumns = (table, customWidths = {}, reservedSpace = 200) => {
  const newSizing = {};
  
  const viewportWidth = window.innerWidth;
  const availableWidth = viewportWidth - reservedSpace;
  
  const visibleColumns = table.getAllLeafColumns().filter(col => 
    col.id !== 'select' && col.getIsVisible()
  );
  
  // Calculate total optimal width
  let totalOptimalWidth = 0;
  visibleColumns.forEach(column => {
    const optimalWidth = getOptimalColumnWidth(column.id, customWidths);
    totalOptimalWidth += optimalWidth;
  });
  
  // If total optimal width fits, use it; otherwise scale proportionally
  if (totalOptimalWidth <= availableWidth) {
    visibleColumns.forEach(column => {
      newSizing[column.id] = getOptimalColumnWidth(column.id, customWidths);
    });
  } else {
    const scaleFactor = availableWidth / totalOptimalWidth;
    
    visibleColumns.forEach(column => {
      const optimalWidth = getOptimalColumnWidth(column.id, customWidths);
      const scaledWidth = Math.floor(optimalWidth * scaleFactor);
      const minWidth = getMinColumnWidth(column.id);
      
      newSizing[column.id] = Math.max(minWidth, scaledWidth);
    });
  }
  
  table.setColumnSizing(newSizing);
};

/**
 * Auto-resize single column to optimal width
 * @param {object} column - Column instance
 * @param {object} table - TanStack table instance
 * @param {object} customWidths - Optional custom optimal widths
 */
export const autoResizeColumn = (column, table, customWidths = {}) => {
  const optimalWidth = getOptimalColumnWidth(column.id, customWidths);
  
  table.setColumnSizing(prev => ({
    ...prev,
    [column.id]: optimalWidth,
  }));
};

/**
 * Reset all column widths to default
 * @param {object} table - TanStack table instance
 */
export const resetColumnWidths = (table) => {
  table.resetColumnSizing();
};
